import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Typography,
    Chip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    TablePagination,
    Skeleton,
    Card,
    CardContent,
    Grid,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Stack,
    Autocomplete,
    Modal,
    IconButton,
} from "@mui/material";
import {
    Schedule as ScheduleIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Refresh as RefreshIcon,
    Visibility as VisibilityIcon,
} from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import productService from "../../../../services/productService";
import { FaSearch } from "react-icons/fa";
import { getMarketPlaces } from "../../../../redux/actions/marketplacesActions";
import { getEcommercePlaces } from "../../../../redux/actions/ecommercePlaceActions";
import { CalendarIcon } from "@mui/x-date-pickers";

const ProductTransfers = () => {
    const userId = useSelector((state) => state?.auth?.user?.id);
    const dispatch = useDispatch();

    const [transfers, setTransfers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [marketplaces, setMarketplaces] = useState([]);
    const [ecommerces, setEcommerces] = useState([]);

    // Filtre state'leri
    const [productNameFilter, setProductNameFilter] = useState("");
    const [marketplaceFilter, setMarketplaceFilter] = useState(null);
    const [ecommerceFilter, setEcommerceFilter] = useState(null);
    const [statusFilter, setStatusFilter] = useState(null);
    const [dateFromFilter, setDateFromFilter] = useState("");
    const [dateToFilter, setDateToFilter] = useState("");
    const [responseModalOpen, setResponseModalOpen] = useState(false);
    const [responseModalContent, setResponseModalContent] = useState("");

    const statusConfig = {
        2: { icon: ScheduleIcon, color: "warning", label: "Kontrol ediliyor" },
        1: { icon: CheckCircleIcon, color: "success", label: "Başarılı" },
        3: { icon: CheckCircleIcon, color: "success", label: "Güncellendi" },
        9: { icon: ErrorIcon, color: "error", label: "Hata" }
    };

    const statusOptions = [
        { value: 1, label: "Başarılı" },
        { value: 3, label: "Güncellendi" },
        { value: 9, label: "Hata" }
    ];

    const getProductTransfer = async () => {
        try {
            setLoading(true);
            const response = await productService.getAllProductTransfer({
                userId: userId,
                page: page + 1,
                pageSize: rowsPerPage,
                search: searchTerm,
                productName: productNameFilter,
                marketPlaceId: marketplaceFilter?.value,
                ecommercePlaceId: ecommerceFilter?.value,
                status: statusFilter?.value,
                startDate: dateFromFilter,
                endDate: dateToFilter
            });
            if (response?.data?.data?.data) {
                setTransfers(response.data.data.data);
                setTotalCount(response.data.data.recordsTotal);
            }
        } catch (error) {
            console.error("error", error);
        } finally {
            setLoading(false);
        }
    };

    // Marketplace ve ecommerce verilerini yükle
    useEffect(() => {
        const loadMarketplaces = async () => {
            try {
                // Marketyerlerini yükle
                const marketResponse = await dispatch(getMarketPlaces());
                if (marketResponse.success) {
                    const data = marketResponse?.data.map((x) => ({
                        value: x.id,
                        label: x.name,
                    }));
                    setMarketplaces(data);
                }

                // E-ticaret platformlarını yükle
                const ecommerceResponse = await dispatch(getEcommercePlaces());
                if (ecommerceResponse.success) {
                    const data = ecommerceResponse?.data.map((x) => ({
                        value: x.id,
                        label: x.name,
                    }));
                    setEcommerces(data);
                }
            } catch (error) {
                console.error("Marketplace/Ecommerce yüklenirken hata:", error);
            }
        };

        loadMarketplaces();
    }, [dispatch]);

    useEffect(() => {
        if (userId) {
            getProductTransfer();
        }
    }, [userId, page, rowsPerPage]);

    const handleSearch = () => {
        setPage(0);
        getProductTransfer();
    };

    const handleRefresh = async () => {
        // State'leri temizle
        setProductNameFilter("");
        setMarketplaceFilter(null);
        setEcommerceFilter(null);
        setStatusFilter(null);
        setDateFromFilter("");
        setDateToFilter("");
        setSearchTerm("");
        setPage(0);

        try {
            setLoading(true);
            const response = await productService.getAllProductTransfer({
                userId: userId,
                page: 1,
                pageSize: rowsPerPage,
                search: "",
                productName: "",
                marketPlaceId: "",
                ecommercePlaceId: "",
                status: "",
                startDate: "",
                endDate: ""
            });
            if (response?.data?.data?.data) {
                setTransfers(response.data.data.data);
                setTotalCount(response.data.data.recordsTotal);
            }
        } catch (error) {
            console.error("error", error);
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const formatDate = (dateString) => {
        if (!dateString || dateString === "0001-01-01T00:00:00") return "Belirtilmemiş";
        return new Date(dateString).toLocaleDateString('tr-TR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleOpenResponseModal = (row) => {
        const raw = row?.response ?? row?.responseData ?? row?.apiResponse ?? null;
        if (raw == null || raw === "") {
            setResponseModalContent("Response verisi bulunamadı.");
        } else {
            try {
                const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
                setResponseModalContent(JSON.stringify(parsed, null, 2));
            } catch {
                setResponseModalContent(typeof raw === "string" ? raw : JSON.stringify(raw, null, 2));
            }
        }
        setResponseModalOpen(true);
    };

    const handleCloseResponseModal = () => {
        setResponseModalOpen(false);
        setResponseModalContent("");
    };

    const getStatusDisplay = (statusId) => {
        const status = statusConfig[statusId];
        if (!status) return statusId;

        const IconComponent = status.icon;
        return (
            <Chip
                icon={<IconComponent />}
                label={status.label}
                color={status.color}
                size="small"
            />
        );
    };


    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography variant="h4" gutterBottom>
                    Ürün Transferleri
                </Typography>
                <Grid container spacing={3}>
                    {[...Array(6)].map((_, index) => (
                        <Grid item xs={12} key={index}>
                            <Card>
                                <CardContent>
                                    <Skeleton variant="text" height={40} />
                                    <Skeleton variant="text" height={20} />
                                    <Skeleton variant="rectangular" height={60} sx={{ mt: 2 }} />
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    }

    const outlinedButtonSx = {
        textTransform: "none",
        fontWeight: 500,
        color: "#1565C0",
        borderColor: "#90CAF9",
        backgroundColor: "#fff",
        borderRadius: "10px",
        "&:hover": {
            borderColor: "#1565C0",
            backgroundColor: "#E3F2FD",
        },
        padding: { xs: "6px 16px", sm: "8px 20px", md: "10px 24px" },
        fontSize: { xs: "0.875rem", sm: "0.875rem", md: "1rem" },
        height: { xs: "36px", sm: "40px", md: "44px" },
    };
    const containedButtonSx = {
        textTransform: "none",
        fontWeight: 500,
        backgroundColor: "#1565C0",
        color: "#FFFFFF",
        borderRadius: "10px",
        "&:hover": {
            backgroundColor: "#0D47A1",
        },
        "&:disabled": {
            backgroundColor: "#90CAF9",
        },
        padding: { xs: "6px 16px", sm: "8px 20px", md: "10px 24px" },
        fontSize: { xs: "0.875rem", sm: "0.875rem", md: "1rem" },
        height: { xs: "36px", sm: "40px", md: "44px" },
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" sx={{ mb: 2 }}>
                    Ürün Transferleri
                </Typography>

                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: 2, sm: 3 },
                        marginBottom: { xs: 2, sm: 4 },
                        borderRadius: 2,
                        border: "1px solid #e0e0e0",
                        backgroundColor: "#F7F9FC",
                        opacity: 1,
                        transition: "opacity 0.2s ease",
                    }}
                >
                    <Box sx={{ marginBottom: { xs: 2, sm: 3 } }}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                color: "#1F1B45",
                                fontSize: { xs: "1rem", sm: "1.1rem" },
                            }}
                        >
                            Filtreler
                        </Typography>
                    </Box>

                    <Grid container spacing={2}>
                        {/* İlk satır - Ürün Adı, Pazaryeri, E-Ticaret */}
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                label="Ürün Adı"
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={productNameFilter}
                                onChange={(e) => setProductNameFilter(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <Autocomplete
                                options={marketplaces}
                                getOptionLabel={(option) => option.label}
                                value={marketplaceFilter}
                                onChange={(event, newValue) => setMarketplaceFilter(newValue)}
                                size="small"
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Pazaryeri Platformları"
                                        variant="outlined"
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <Autocomplete
                                options={ecommerces}
                                getOptionLabel={(option) => option.label}
                                value={ecommerceFilter}
                                onChange={(event, newValue) => setEcommerceFilter(newValue)}
                                size="small"
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="E-Ticaret Platformları"
                                        variant="outlined"
                                    />
                                )}
                            />
                        </Grid>

                        {/* İkinci satır - Durum, Tarih aralığı */}
                        <Grid item xs={12} sm={6} md={4}>
                            <Autocomplete
                                options={statusOptions}
                                getOptionLabel={(option) => option.label}
                                value={statusFilter}
                                onChange={(event, newValue) => setStatusFilter(newValue)}
                                size="small"
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Durum"
                                        variant="outlined"
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={8}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <TextField
                                    label="Başlangıç Tarihi"
                                    type="date"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dateFromFilter}
                                    onChange={(e) => setDateFromFilter(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        '& .MuiInputBase-input': {
                                            padding: '8px 12px',
                                        }
                                    }}
                                />
                                <Typography sx={{ color: '#666', fontSize: 20 }}>-</Typography>
                                <TextField
                                    label="Bitiş Tarihi"
                                    type="date"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dateToFilter}
                                    onChange={(e) => setDateToFilter(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        '& .MuiInputBase-input': {
                                            padding: '8px 12px',
                                        }
                                    }}
                                />
                            </Stack>
                        </Grid>

                        {/* Butonlar: Yenile, Ara, Temizle – Products sayfası buton stili */}
                        <Grid item xs={12}>
                            <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: "wrap" }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<RefreshIcon />}
                                    onClick={handleRefresh}
                                    disabled={loading}
                                    sx={outlinedButtonSx}
                                >
                                    Yenile
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<FaSearch />}
                                    onClick={handleSearch}
                                    disabled={loading}
                                    sx={containedButtonSx}
                                >
                                    Ara
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={handleRefresh}
                                    disabled={loading}
                                    sx={outlinedButtonSx}
                                >
                                    Temizle
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Paper>
            </Box>

            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="product transfers table">
                    <TableHead>
                        <TableRow>
                            <TableCell>Ürün</TableCell>
                            <TableCell align="center">Pazaryeri</TableCell>
                            <TableCell align="center">Durum</TableCell>
                            <TableCell align="center">Detay</TableCell>
                            <TableCell align="center">Pazaryeri Cevabı</TableCell>
                            <TableCell align="center">Tarih</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {transfers.map((row) => (
                            <TableRow
                                key={row.id}
                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                            >
                                <TableCell component="th" scope="row">
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <img
                                            src={row.product.productImages[0]?.imageUrl}
                                            alt={row.product.title}
                                            referrerPolicy="no-referrer"
                                            style={{
                                                width: 50,
                                                height: 50,
                                                marginRight: 12,
                                                objectFit: 'cover',
                                                borderRadius: 4
                                            }}
                                        />
                                        <Box>
                                            <Typography variant="body1" fontWeight="medium">
                                                {row.product.title}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                Stok kodu: {row.product.stockCode}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell align="center">
                                    <img
                                        src={row?.ecommercePlace?.iconLink || row?.marketPlace?.iconLink}
                                        alt="Pazaryeri"
                                        referrerPolicy="no-referrer"
                                        style={{ width: 40, height: 40 }}
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    {getStatusDisplay(row.status)}
                                </TableCell>
                                <TableCell align="center">
                                    <Typography variant="body2">
                                        {row.userDescription || "-"}
                                    </Typography>
                                </TableCell>
                                <TableCell align="center">
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<VisibilityIcon />}
                                        onClick={() => handleOpenResponseModal(row)}
                                        sx={{
                                            textTransform: "none",
                                            fontWeight: 500,
                                            color: "#1565C0",
                                            borderColor: "#90CAF9",
                                            borderRadius: "10px",
                                            "&:hover": {
                                                borderColor: "#1565C0",
                                                backgroundColor: "#E3F2FD",
                                            },
                                            padding: "4px 12px",
                                            fontSize: "0.8125rem",
                                        }}
                                    >
                                        Detay
                                    </Button>
                                </TableCell>
                                <TableCell align="center">
                                    <Typography variant="body2">
                                        {formatDate(row.createDate)}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Sayfa başına satır:"
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} / ${count !== -1 ? count : `${to}'den fazla`}`
                    }
                />
            </TableContainer>

            {/* Response JSON modal */}
            <Modal
                open={responseModalOpen}
                onClose={handleCloseResponseModal}
                aria-labelledby="response-modal-title"
                aria-describedby="response-modal-content"
                sx={{
                    "& .MuiBackdrop-root": {
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                    },
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: { xs: "95%", sm: "90%", md: "75%" },
                        maxWidth: 720,
                        maxHeight: "85vh",
                        bgcolor: "background.paper",
                        boxShadow: 24,
                        borderRadius: 2,
                        border: "1px solid #E3F2FD",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            px: 2,
                            py: 1.5,
                            borderBottom: "1px solid #E0E0E0",
                            backgroundColor: "#E3F2FD",
                        }}
                    >
                        <Typography
                            id="response-modal-title"
                            variant="h6"
                            component="h2"
                            sx={{ fontWeight: 600, color: "#0D47A1", fontSize: "1.1rem" }}
                        >
                            Response (JSON)
                        </Typography>
                        <IconButton
                            aria-label="kapat"
                            onClick={handleCloseResponseModal}
                            sx={{
                                color: "#1565C0",
                                "&:hover": { backgroundColor: "rgba(21, 101, 192, 0.08)" },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Box
                        id="response-modal-content"
                        sx={{
                            p: 2,
                            overflow: "auto",
                            flex: 1,
                            backgroundColor: "#FAFAFA",
                            "&::-webkit-scrollbar": { width: 8 },
                            "&::-webkit-scrollbar-track": { background: "#f1f1f1", borderRadius: 4 },
                            "&::-webkit-scrollbar-thumb": { background: "#888", borderRadius: 4 },
                        }}
                    >
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                backgroundColor: "#fff",
                                borderColor: "#E0E0E0",
                            }}
                        >
                            <Box
                                component="pre"
                                sx={{
                                    margin: 0,
                                    fontFamily: '"Roboto Mono", "Consolas", "Monaco", monospace',
                                    fontSize: "0.8125rem",
                                    lineHeight: 1.6,
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                    color: "#424242",
                                }}
                            >
                                {responseModalContent}
                            </Box>
                        </Paper>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};

export default ProductTransfers;